import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Ticket, ArrowLeft, ArrowRight, Envelope, Lock, Eye, EyeSlash } from "@phosphor-icons/react";

/**
 * /redeem — public onboarding page for users with a MedEasy gift/promo code.
 *
 * Bypasses Stripe entirely: enter email + password + LIFE-/YEAR- code.
 * Backend endpoint `POST /api/auth/register-with-token` creates the account
 * AND grants the correct subscription tier in a single atomic operation.
 * On success we set the JWT cookie server-side, refresh the auth context,
 * and land the user on /log.
 */
export default function Redeem() {
  const navigate = useNavigate();
  const { refresh, isAuthed, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // If someone lands here already signed in, send them straight to the
  // Settings redemption box — they can still redeem there, and we avoid
  // creating a second stranded account.
  useEffect(() => {
    if (!loading && isAuthed) navigate("/settings", { replace: true });
  }, [isAuthed, loading, navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim().toUpperCase();
    if (!trimmedEmail || !password || !trimmedCode) {
      toast.error("Fill in all three fields to continue");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register-with-token", {
        email: trimmedEmail,
        password,
        code: trimmedCode,
      });
      toast.success(`Code redeemed! Your ${data.tier_label} access is now active.`);
      try { await refresh(); } catch { /* best-effort */ }
      setTimeout(() => navigate("/log", { replace: true }), 500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not redeem that code";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex flex-col">
      {/* Top bar */}
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          data-testid="redeem-back-home"
          className="flex items-center gap-2 text-[#5C6660] hover:text-[#1E2420] transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <button
          onClick={() => navigate("/login")}
          data-testid="redeem-signin-link"
          className="text-sm text-[#5C6660] hover:text-[#1E2420] transition-colors"
        >
          Have an account? <span className="text-[#2C4C3B] font-semibold underline">Sign in</span>
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <Card className="w-full max-w-md rounded-3xl border-[#E3E1D9] shadow-xl shadow-[#2C4C3B]/5">
          <CardContent className="p-7 sm:p-9">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-[#C17767] flex items-center justify-center shrink-0">
                <Ticket weight="fill" size={22} className="text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6660] font-semibold">
                  MedEasy · Gift redemption
                </div>
                <h1 className="font-[Manrope] font-bold text-2xl text-[#1E2420] leading-tight">
                  Redeem your code
                </h1>
              </div>
            </div>

            <p className="text-sm text-[#5C6660] leading-relaxed mb-6">
              Have a <strong className="text-[#1E2420]">LIFE-</strong> or <strong className="text-[#1E2420]">YEAR-</strong> code? Create your account below and we&apos;ll unlock MedEasy immediately — no credit card, no Stripe.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5C6660] font-semibold mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B7B3A5]" />
                  <input
                    data-testid="redeem-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full h-11 pl-9 pr-3 rounded-lg border border-[#E3E1D9] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/40"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5C6660] font-semibold mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B7B3A5]" />
                  <input
                    data-testid="redeem-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full h-11 pl-9 pr-10 rounded-lg border border-[#E3E1D9] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/40"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="redeem-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6660] hover:text-[#1E2420]"
                  >
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5C6660] font-semibold mb-1.5">
                  Redemption code
                </label>
                <div className="relative">
                  <Ticket size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B7B3A5]" />
                  <input
                    data-testid="redeem-code-input"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="LIFE-XXXX-XXXX or YEAR-XXXX-XXXX"
                    autoComplete="off"
                    spellCheck={false}
                    className="w-full h-11 pl-9 pr-3 rounded-lg border border-[#E3E1D9] bg-white text-sm font-mono tracking-wider uppercase placeholder:text-[#B7B3A5] placeholder:font-sans placeholder:tracking-normal placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-[#C17767]/40"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="redeem-submit"
                disabled={busy}
                className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold mt-2"
              >
                {busy ? "Redeeming…" : (
                  <>Create account &amp; unlock <ArrowRight size={18} className="ml-2" /></>
                )}
              </Button>
            </form>

            <p className="text-[11px] text-[#5C6660] mt-5 leading-relaxed">
              One-time use. LIFE-codes unlock permanent lifetime access. YEAR-codes unlock 365 days. You can cancel anytime — there&apos;s no card on file.
            </p>
          </CardContent>
        </Card>
      </main>

      <footer className="px-6 sm:px-10 py-6 text-center text-xs text-[#5C6660]">
        <button onClick={() => navigate("/terms")} className="hover:text-[#1E2420] underline" data-testid="redeem-footer-terms">Terms</button>
        <span className="mx-2 text-[#E3E1D9]">·</span>
        <button onClick={() => navigate("/privacy")} className="hover:text-[#1E2420] underline" data-testid="redeem-footer-privacy">Privacy</button>
      </footer>
    </div>
  );
}
